import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Check, Copy, Globe, Code2, Webhook, ExternalLink } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { BrowserAnalyticsSettings } from '@/components/organization/BrowserAnalyticsSettings';

interface TechnicalInstallGuideProps {
  orgId?: string;
}

// ---------------------------------------------------------------------------
// Code block with inline copy
// ---------------------------------------------------------------------------
const CodeBlock: React.FC<{ code: string; label: string }> = ({ code, label }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast({ title: `${label} copied to clipboard` });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({
        title: 'Copy failed',
        description: 'Please select and copy manually.',
        variant: 'destructive',
      });
    }
  }, [code, label]);

  return (
    <div className="group relative rounded-lg border border-border/60 bg-muted/30 overflow-hidden">
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-border/40 bg-muted/20">
        <span className="text-[11px] font-medium text-muted-foreground/70 uppercase tracking-wider">
          {label}
        </span>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-[11px] gap-1 text-muted-foreground hover:text-foreground"
          onClick={handleCopy}
        >
          <AnimatePresence mode="wait" initial={false}>
            {copied ? (
              <motion.span
                key="check"
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.5, opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="flex items-center gap-1 text-[hsl(var(--success))]"
              >
                <Check className="h-3 w-3" />
                Copied
              </motion.span>
            ) : (
              <motion.span
                key="copy"
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.5, opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="flex items-center gap-1"
              >
                <Copy className="h-3 w-3" />
                Copy
              </motion.span>
            )}
          </AnimatePresence>
        </Button>
      </div>
      <pre className="p-3 text-[13px] leading-relaxed overflow-x-auto font-mono text-foreground/85">
        <code>{code}</code>
      </pre>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Step data
// ---------------------------------------------------------------------------
interface Step {
  number: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  content: (props: { orgId?: string; navigate: ReturnType<typeof useNavigate> }) => React.ReactNode;
}

const buildSteps = (scriptSource: string): Step[] => [
  {
    number: 1,
    title: 'Configure custom domain',
    description: 'Point your subdomain to Hobbes so your agent loads from your own domain.',
    icon: <Globe className="h-4 w-4" />,
    content: ({ navigate }) => (
      <div className="space-y-3">
        <div className="rounded-lg border border-border/60 bg-muted/20 p-3">
          <p className="text-sm text-muted-foreground leading-relaxed">
            Create a <code className="px-1 py-0.5 rounded bg-muted text-xs font-mono">CNAME</code> record
            pointing your chosen subdomain (e.g. <code className="px-1 py-0.5 rounded bg-muted text-xs font-mono">agent.yoursite.com</code>)
            to <code className="px-1 py-0.5 rounded bg-muted text-xs font-mono">agent.hihobbes.com</code>, then register it in your org settings.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5"
          onClick={() => navigate('/organization-settings?tab=domain')}
        >
          Open domain settings
          <ExternalLink className="h-3 w-3" />
        </Button>
      </div>
    ),
  },
  {
    number: 2,
    title: 'Install the agent script',
    description: "Drop the script tag on your site to add Hobbes. By default we render the Launcher CTA at bottom-center — wire your own button if you'd rather control the trigger.",
    icon: <Code2 className="h-4 w-4" />,
    content: ({ orgId }) => (
      <div className="space-y-3">
        <CodeBlock
          label="Script tag"
          code={`<script
  src="${scriptSource}"
  client-key="${orgId || 'your-org-id'}"
  mode="sales"
  async
></script>`}
        />
        <p className="text-sm text-muted-foreground leading-relaxed">
          Want users to open the demo from your own button instead? Add{' '}
          <code className="px-1 py-0.5 rounded bg-muted text-xs font-mono">trigger-selector</code>{' '}
          and a matching element on your page:
        </p>
        <CodeBlock
          label="Custom trigger (optional)"
          code={`<script
  src="${scriptSource}"
  client-key="${orgId || 'your-org-id'}"
  mode="sales"
  trigger-selector="#hobbes-trigger"
  async
></script>

<button id="hobbes-trigger" type="button">
  Talk to sales
</button>`}
        />
      </div>
    ),
  },
  {
    number: 3,
    title: 'Set up webhook delivery',
    description: 'Route qualified leads and meeting bookings to your CRM in real time.',
    icon: <Webhook className="h-4 w-4" />,
    content: ({ navigate }) => (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground leading-relaxed">
          Configure an HTTPS endpoint to receive event payloads. Here's an example of what Hobbes sends
          when a visitor is qualified:
        </p>
        <CodeBlock
          label="Example payload"
          code={`{
  "event_type": "lead_qualified",
  "session": {
    "id": "session_123",
    "email": "buyer@example.com",
    "tracking": {
      "utm_source": "google",
      "utm_medium": "cpc",
      "utm_campaign": "spring-demo",
      "gclid": "sample-gclid",
      "referrer_url": "https://www.google.com/",
      "landing_page_url": "https://www.example.com/demo",
      "device": "desktop",
      "browser": "Chrome",
      "os": "macOS"
    }
  },
  "sales_summary": {
    "qualification_status": "qualified",
    "next_step": "book_demo"
  }
}`}
        />
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5"
          onClick={() => navigate('/integrations/webhooks')}
        >
          Open webhook settings
          <ExternalLink className="h-3 w-3" />
        </Button>
      </div>
    ),
  },
];

// ---------------------------------------------------------------------------
// Individual step row
// ---------------------------------------------------------------------------
const StepRow: React.FC<{
  step: Step;
  isLast: boolean;
  orgId?: string;
}> = ({ step, isLast, orgId }) => {
  const navigate = useNavigate();

  return (
    <div className="relative flex gap-4">
      {/* Vertical connector line */}
      {!isLast && (
        <div className="absolute left-[17px] top-[40px] w-px bottom-0 bg-border" />
      )}

      {/* Step number indicator */}
      <div className="relative z-10 flex-shrink-0 w-[36px] h-[36px] rounded-full border-2 border-border bg-card flex items-center justify-center">
        <span className="text-sm font-semibold text-muted-foreground">
          {step.number}
        </span>
      </div>

      {/* Step content */}
      <div className={`flex-1 min-w-0 ${isLast ? '' : 'pb-8'}`}>
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">{step.icon}</span>
          <h3 className="text-sm font-semibold text-foreground">{step.title}</h3>
        </div>
        <p className="text-sm text-muted-foreground mt-0.5 leading-relaxed">
          {step.description}
        </p>
        <div className="pt-3">
          {step.content({ orgId, navigate })}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export const TechnicalInstallGuide: React.FC<TechnicalInstallGuideProps> = ({ orgId }) => {
  const scriptSource =
    window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? 'http://localhost:3334/widget.js'
      : 'https://agent.hihobbes.com/widget.js';

  const steps = buildSteps(scriptSource);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
    >
      <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
        Complete these steps to get your domain, agent, and lead notifications live.
      </p>

      <Tabs defaultValue="agent-install" className="space-y-6">
        <TabsList className="h-9 rounded-lg bg-muted/50 p-1">
          <TabsTrigger value="agent-install" className="h-7 rounded-md px-3 text-[13px]">
            Agent
          </TabsTrigger>
          <TabsTrigger value="browser-analytics" className="h-7 rounded-md px-3 text-[13px]">
            Analytics
          </TabsTrigger>
        </TabsList>

        <TabsContent value="agent-install" className="mt-0">
          <div>
            {steps.map((step, i) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.3,
                  delay: i * 0.08,
                  ease: [0.16, 1, 0.3, 1],
                }}
              >
                <StepRow
                  step={step}
                  isLast={i === steps.length - 1}
                  orgId={orgId}
                />
              </motion.div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="browser-analytics" className="mt-0">
          {orgId ? (
            <BrowserAnalyticsSettings orgId={orgId} />
          ) : (
            <p className="text-sm text-muted-foreground">Select an organization to configure analytics.</p>
          )}
        </TabsContent>
      </Tabs>
    </motion.div>
  );
};
